// V2 Driver Change section, rendered inside the "What changed?" drawer.
//
// The mode (Add / Delete) is already chosen on the board, so this component
// does NOT re-show the mode selector. Instead:
//   - Add mode  -> immediately opens the Add Driver form (AutomobileDriverRightPanel)
//                  and lists the added drivers in a table for edit/delete.
//   - Delete mode -> shows a dropdown to pick an existing driver to remove.
//
// Kept separate from the shared V1 DriverInformation so V1 is unaffected.

import React from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
} from "../../../../../../../../components/formsComponents";
import { StandardTableActions } from "../../../../../../../../components/StandardTableActions/StandardTableActions";
import produce from "immer";
import { IAutomobilePolicyChangeProps } from "../../Steps/AutomobilePolicyChange.types";
import { useAutomobilePolicyDriverInfo } from "../../../hooks/useAutomobilePolicyDriverInfo";
import { IAutomobileDriverInformation } from "../../../../../../../../redux/automobileDriver/automobileDriver.types";
import { AutomobileDriverRightPanel } from "../../../AutomobileDriver/AutomobileDriverInformation/AutomobileDriverRightPanel";
import { ConfirmDialog } from "../../../../../../../../components/Dialogs/ConfirmDialog";
import { AddOutlined } from "@mui/icons-material";

interface IDriverInformationV2Props extends IAutomobilePolicyChangeProps {
  // "Add" | "Delete" chosen on the board.
  mode?: string | null;
  // When true, automatically opens the Add Driver panel on entering Add mode
  // with no records. Enabled for the dedicated Driver drawer, disabled inside
  // Multiple Change (where several sections share one drawer and an auto-popup
  // would open a confusing second drawer on top).
  autoOpen?: boolean;
  // z-index for the Add/Edit Driver panel. Needed when this section lives
  // inside the Multiple Change dialog so the driver panel renders on top of
  // that dialog instead of behind it.
  driverPanelZIndex?: number;
}

export const DriverInformationV2: React.FC<IDriverInformationV2Props> = ({
  values,
  setValues,
  handleChange,
  setFieldValue,
  customerId,
  customerPolicyId,
  isStatusCompleted,
  autoSaveData = () => {},
  mode,
  autoOpen = false,
  driverPanelZIndex,
}) => {
  const [open, setOpen] = React.useState<{
    type: "create" | "edit" | "view";
    index?: number;
    data?: IAutomobileDriverInformation;
  } | null>(null);
  const { driverDropDown, refreshList } =
    useAutomobilePolicyDriverInfo(customerPolicyId);

  // Effective mode: prefer the mode passed from the board, fall back to the
  // value already stored on the record.
  const effectiveMode = mode || values.driver_change.type || "Add";

  // Keep driver_change.type in sync with the chosen mode.
  React.useEffect(() => {
    if (effectiveMode && values.driver_change.type !== effectiveMode) {
      setFieldValue("driver_change.type", effectiveMode);
    }
  }, [effectiveMode]);

  const handleSaveSuccess = (driver: IAutomobileDriverInformation) => {
    const newValues = produce(values, (draftValues) => {
      const updatedData = {
        ...driver,
        customer_policy_id: customerPolicyId ?? null,
        customer_id: customerId ? Number(customerId) : null,
      };
      if (open && open.type === "edit" && open.index !== undefined) {
        draftValues.driver_change.records[open.index] = updatedData;
      } else {
        draftValues.driver_change.records.push(updatedData);
      }
    });
    autoSaveData(newValues);
    setValues(newValues);
    setOpen(null);
    refreshList();
  };

  const handleCreateEdit = (index?: number) => () => {
    if (isStatusCompleted) {
      return;
    }
    setOpen({
      type: index !== undefined ? "edit" : "create",
      index,
      data:
        index !== undefined ? values.driver_change.records[index] : undefined,
    });
  };

  const handleView = (index: number) => () => {
    setOpen({
      type: "view",
      index,
      data: values.driver_change.records[index],
    });
  };

  const [deleteConfirm, setDeleteConfirm] = React.useState<number | null>(null);

  const handleDeleteConfirm = () => {
    if (deleteConfirm === null) {
      return;
    }
    const newValues = produce(values, (draftValues) => {
      draftValues.driver_change.records.splice(deleteConfirm, 1);
    });
    setValues(newValues);
    autoSaveData(newValues);
    setDeleteConfirm(null);
  };

  const isAddMode = ["Add", "Change"].includes(effectiveMode);

  // Auto-open the Add Driver form the first time we enter Add mode with no
  // records yet, so clicking "Add" on the board lands directly on the form.
  const autoOpenedRef = React.useRef(false);
  React.useEffect(() => {
    if (
      autoOpen &&
      isAddMode &&
      !autoOpenedRef.current &&
      !isStatusCompleted &&
      values.driver_change.records.length === 0
    ) {
      autoOpenedRef.current = true;
      setOpen({ type: "create" });
    }
  }, [isAddMode]);

  return (
    <>
      {isAddMode && (
        <Box sx={{ mt: 1 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              Drivers
            </Typography>
            <Button
              variant="contained"
              size="small"
              disabled={isStatusCompleted}
              onClick={handleCreateEdit()}
              startIcon={<AddOutlined />}
            >
              Add Driver
            </Button>
          </Stack>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 1 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: 700, width: 120 }}>
                    Action
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 56 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Driver Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>License</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {values.driver_change.records.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      align="center"
                      sx={{ py: 3, color: "text.secondary" }}
                    >
                      No drivers added yet. Click "Add Driver" to add one.
                    </TableCell>
                  </TableRow>
                ) : (
                  values.driver_change.records.map((item, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <StandardTableActions
                          onViewClick={handleView(index)}
                          onEditClick={
                            !isStatusCompleted
                              ? handleCreateEdit(index)
                              : undefined
                          }
                          onDeleteClick={
                            !isStatusCompleted
                              ? () => setDeleteConfirm(index)
                              : undefined
                          }
                        />
                      </TableCell>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{item.driver_name || "--"}</TableCell>
                      <TableCell>{item.license_number || "--"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {effectiveMode === "Delete" && (
        <Grid container>
          <Grid item xs={12} md={6}>
            <CustomFormLabel>Select Driver to Delete</CustomFormLabel>
            <ControlledCustomSelect
              variant="outlined"
              size="small"
              type="string"
              name="driver_change.delete_driver"
              fullWidth
              displayEmpty
              value={Number(values.driver_change.delete_driver)}
              onChange={handleChange}
              placeholder="Select One"
              options={driverDropDown}
              // Rendered inside the V2 right-side drawer (zIndex 1400); the menu
              // portals to the body at the default modal zIndex (1300) and would
              // open behind the drawer. Keep it above the drawer.
              MenuProps={{ sx: { zIndex: 1600 } }}
            />
          </Grid>
        </Grid>
      )}

      {open && (
        <AutomobileDriverRightPanel
          open={true}
          readonly={open.type === "view"}
          customerId={customerId}
          customerPolicyId={customerPolicyId}
          driverNumber={open.data ? (open.data.driver_number as any) : undefined}
          onSaveSuccess={handleSaveSuccess}
          staticStatusValue="ACTIVE"
          created_from="Policy_Change"
          onClose={() => setOpen(null)}
          zIndex={driverPanelZIndex}
        />
      )}

      {deleteConfirm !== null && (
        <ConfirmDialog
          open
          content="Are you sure you want to delete this record?"
          onClose={() => setDeleteConfirm(null)}
          onConfrim={handleDeleteConfirm}
        />
      )}
    </>
  );
};
